import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ConfirmDialogVariant = 'danger' | 'warning' | 'default';

export interface ConfirmDialogOptions {
  title?: string;
  message: string;
  details?: string[];
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmDialogVariant;
}

export interface ConfirmDialogState extends Required<Omit<ConfirmDialogOptions, 'details'>> {
  details: string[];
}

@Injectable({
  providedIn: 'root'
})
export class ConfirmDialogService {
  private readonly stateSubject = new BehaviorSubject<ConfirmDialogState | null>(null);
  readonly state$ = this.stateSubject.asObservable();
  private resolveFn: ((value: boolean) => void) | null = null;

  confirm(options: ConfirmDialogOptions): Observable<boolean> {
    return new Observable((observer) => {
      this.resolveFn = (value: boolean) => {
        observer.next(value);
        observer.complete();
      };

      this.stateSubject.next({
        title: options.title ?? 'Confirm',
        message: options.message,
        details: options.details ?? [],
        confirmLabel: options.confirmLabel ?? 'Confirm',
        cancelLabel: options.cancelLabel ?? 'Cancel',
        variant: options.variant ?? 'default',
      });

      document.body.style.overflow = 'hidden';
    });
  }

  confirmDelete(message: string, options?: Partial<ConfirmDialogOptions>): Observable<boolean> {
    return this.confirm({
      title: options?.title ?? 'Delete?',
      message,
      details: options?.details,
      confirmLabel: options?.confirmLabel ?? 'Delete',
      cancelLabel: options?.cancelLabel ?? 'Cancel',
      variant: 'danger',
    });
  }

  accept(): void {
    this.resolveFn?.(true);
    this.close();
  }

  dismiss(): void {
    this.resolveFn?.(false);
    this.close();
  }

  private close(): void {
    this.resolveFn = null;
    this.stateSubject.next(null);
    document.body.style.overflow = '';
  }
}
